"use client";

import * as React from "react";
import Link from "next/link";
import { Icon } from "@/components/ui/icon";

import { Container } from "@/app/_components/layout/container";
import { faqBlock } from "@/lib/content/homepage";
import { trackEvent } from "@/lib/analytics/dataLayer";
import { cn } from "@/lib/utils";

export function FAQ() {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);

  const toggle = (i: number) => {
    // Fire only on the closed -> open transition (not when collapsing). The
    // push lives in the event handler (not the state updater) so it runs once
    // per click — React Strict Mode double-invokes updaters, not handlers.
    if (openIndex !== i) {
      trackEvent("faq_open", { question: faqBlock.items[i].q });
    }
    setOpenIndex((current) => (current === i ? null : i));
  };

  return (
    <section
      id="faq"
      aria-label="Häufige Fragen"
      className="hidden bg-white text-clarus-dark py-20 md:block md:py-28"
    >
      <Container>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-display-lg text-clarus-dark">
            {faqBlock.heading}
          </h2>
        </div>

        <div className="mt-12 md:mt-16 grid gap-x-16 gap-y-0 md:grid-cols-2">
          {faqBlock.items.map((item, i) => {
            const isOpen = openIndex === i;
            const buttonId = `faq-q-${i}`;
            const panelId = `faq-a-${i}`;
            return (
              <div
                key={item.q}
                /* `group/faq` exposes the row to CSS group-hover so the
                   icon AND the answer panel react together. The item
                   stays a click-toggle for keyboard + touch users, but
                   mouse users get a preview-on-hover. */
                className="group/faq border-b border-clarus-dark/15 py-5 md:py-6"
              >
                <h3>
                  <button
                    type="button"
                    id={buttonId}
                    aria-controls={panelId}
                    aria-expanded={isOpen}
                    onClick={() => toggle(i)}
                    className="flex w-full cursor-pointer items-center justify-between gap-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clarus-purple focus-visible:ring-offset-2"
                  >
                    <span
                      className="text-clarus-dark"
                      style={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 800,
                        fontSize: "1rem",
                      }}
                    >
                      {item.q}
                    </span>
                    <span
                      aria-hidden="true"
                      className={cn(
                        "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-[1.5px] border-clarus-dark text-clarus-dark transition-transform duration-300 ease-[ease] motion-reduce:transition-none group-hover/faq:rotate-90",
                        isOpen && "rotate-90",
                      )}
                    >
                      <Icon name="add" className="text-[16px]" />
                    </span>
                  </button>
                </h3>
                {/*
                  Smooth accordion via grid-template-rows trick: the outer
                  grid animates from `0fr` to `1fr`, which expands to the
                  natural height of the content with no fixed max-height
                  cap. Hover OR explicit open both expand the panel.
                */}
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  className={cn(
                    "grid transition-[grid-template-rows,margin-top] duration-300 ease-[ease] motion-reduce:transition-none",
                    "group-hover/faq:mt-3 group-hover/faq:grid-rows-[1fr]",
                    isOpen ? "mt-3 grid-rows-[1fr]" : "grid-rows-[0fr]",
                  )}
                >
                  <div className="overflow-hidden">
                    <p
                      className="text-clarus-dark/80"
                      style={{
                        fontFamily: "var(--font-sans)",
                        fontSize: "0.9375rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {item.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-10 text-center">
          <Link
            href="/faq"
            className="text-body text-clarus-dark underline underline-offset-4 hover:opacity-80"
          >
            Alle Fragen im Hilfe-Center ansehen
          </Link>
        </p>
      </Container>
    </section>
  );
}
